/**
 * Location Service (Frontend)
 *
 * API client for location catalog operations.
 * Locations are per-tenant, optionally per-client scene/environment presets.
 */

import { getApiBaseUrl } from '../config/environment';
import { Location } from '../contexts/StudioContext';

const API_BASE = getApiBaseUrl();

export interface ListLocationsResponse {
  locations: Location[];
  total: number;
  clientId?: string;
}

export interface LocationImportItem {
  title: string;
  description: string;
  comfyText: string;
  thumbnail?: string;
}

export interface LocationImportResult {
  imported: number;
  errors: string[];
}

export class LocationService {
  private accessToken: string | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
    return headers;
  }

  /**
   * List locations for the tenant, optionally scoped to a clientId
   */
  async listLocations(options?: {
    clientId?: string;
    search?: string;
    limit?: number;
  }): Promise<ListLocationsResponse> {
    const q = new URLSearchParams();
    if (options?.clientId) q.append('clientId', options.clientId);
    if (options?.search)   q.append('search',   options.search);
    if (options?.limit)    q.append('limit',    options.limit.toString());

    const url = `${API_BASE}/api/locations?${q.toString()}`;
    const response = await fetch(url, { method: 'GET', headers: this.getHeaders() });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list locations' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  /**
   * Bulk import location rows (parsed from CSV on the frontend)
   */
  async importLocations(
    locations: LocationImportItem[],
    clientId?: string
  ): Promise<LocationImportResult> {
    const q = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    const url = `${API_BASE}/api/location-import${q}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ locations }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  /**
   * Delete a single location by ID
   */
  async deleteLocation(locationId: string, clientId?: string): Promise<void> {
    const q = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    const url = `${API_BASE}/api/locations/${encodeURIComponent(locationId)}${q}`;
    const response = await fetch(url, { method: 'DELETE', headers: this.getHeaders() });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete location' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
  }

  /**
   * Delete all locations for the tenant (optionally scoped to a clientId)
   */
  async deleteAllLocations(clientId?: string): Promise<number> {
    const q = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    const url = `${API_BASE}/api/locations${q}`;
    const response = await fetch(url, { method: 'DELETE', headers: this.getHeaders() });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete all locations' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    const data = await response.json();
    return data.deleted as number;
  }

  /**
   * Fetch a Google Sheets URL server-side and import the rows as locations.
   * The sheet must be publicly accessible ("Anyone with the link can view").
   */
  async importFromUrl(sheetUrl: string, clientId?: string): Promise<LocationImportResult> {
    const q = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    const url = `${API_BASE}/api/location-import-url${q}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ url: sheetUrl }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'URL import failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  /**
   * List distinct client IDs that have locations
   */
  async listClients(): Promise<string[]> {
    const url = `${API_BASE}/api/locations-clients`;
    const response = await fetch(url, { method: 'GET', headers: this.getHeaders() });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list clients' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    const data = await response.json();
    return data.clients as string[];
  }
}

export const locationService = new LocationService();
